import React, { useState, memo } from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useSelector, useDispatch } from 'react-redux';
import { setOpenTopicSearchDialog } from '../../reducers/Topic';
import { deleteSearch } from './topicActions';
import EditIcon from '@material-ui/icons/Edit';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import DeleteConfirmationDialog from '../shared/DeleteConfirmationDialog';
import { preventParentClick, setDateRangeToOneYear, setSearchParams, composeNewSearch } from './topicHelpers';
import clsx from 'clsx';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { BeatLoader } from 'global/Spinners';

const fullOpacity = {
  opacity: '1 !important',
  pointerEvents: 'inherit !important',
};

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  label: {
    marginBottom: 0,
    padding: '4px',
    width: '8.6rem',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    fontSize: 12,
  },
  listItem: {
    padding: 0,
    '&:hover + $listItemActionContainer': fullOpacity,
  },
  listItemActionContainer: {
    top: '50%',
    right: '2px',
    position: 'absolute',
    transform: 'translateY(-50%)',
    opacity: 0,
    pointerEvents: 'none',
    '&:hover': fullOpacity,
  },
  fullOpacityForMobile: fullOpacity,
  alternateColor: {
    backgroundColor: '#f8f8f8',
  },
  IconButtonContainer: {
    padding: '8px',
  },
  editIconButton: {
    paddingRight: '4px',
  },
  deleteIconButton: {
    paddingLeft: '4px',
  },
  themeListActionBtn: {
    fontSize: '20px',
  },
}));

const TopicSearchHistory = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [deleteSearchInfo, setDeleteSearchInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const savedSearches = useSelector(state => state.Topic.savedSearches);
  const selectedSearch = useSelector(state => state.Topic.selectedSearch);

  const handleSearch = (e, s, openComposeDialog) => {
    preventParentClick(e);
    setDateRangeToOneYear();
    openComposeDialog && dispatch(setOpenTopicSearchDialog(true));
    setSearchParams(s);
  };

  const handleDeleteSearch = (e, id, name) => {
    preventParentClick(e);
    setDeleteSearchInfo({ id, name });
  };

  const confirmDeleteSearch = async () => {
    setIsLoading(true);
    await dispatch(deleteSearch(deleteSearchInfo.id));
    setIsLoading(false);
    setDeleteSearchInfo(null);
  };

  return (
    <div onClick={e => preventParentClick(e)}>
      {deleteSearchInfo?.id ? (
        <DeleteConfirmationDialog
          searchName={deleteSearchInfo.name}
          closeDialog={() => setDeleteSearchInfo(null)}
          confirmDelete={confirmDeleteSearch}
          isLoading={isLoading}
        />
      ) : null}
      <List component="nav" className={classes.root}>
        {savedSearches?.length ? (
          savedSearches.map((s, index) => {
            return (
              <ListItem
                button
                className={clsx(classes.listItem, {
                  [classes.alternateColor]: index % 2 === 0,
                })}
                key={index}
                selected={!!(selectedSearch && selectedSearch.searchId === s.searchId)}
                onClick={e => handleSearch(e, s)}>
                <ListItemText primary={<p className={classes.label}>{s.searchLabel}</p>} />
                <ListItemSecondaryAction
                  className={clsx(classes.listItemActionContainer, { [classes.fullOpacityForMobile]: isMobile })}>
                  <Tooltip title="Edit Theme">
                    <IconButton
                      className={clsx(classes.IconButtonContainer, classes.editIconButton)}
                      onClick={e => handleSearch(e, s, true)}>
                      <EditIcon className={classes.themeListActionBtn} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Theme">
                    <IconButton
                      className={clsx(classes.IconButtonContainer, classes.deleteIconButton)}
                      onClick={e => handleDeleteSearch(e, s.searchId, s.searchLabel)}>
                      <DeleteForeverIcon className={classes.themeListActionBtn} />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })
        ) : savedSearches?.length === 0 ? (
          <p>
            You don't have any saved topics yet. <br></br>
            <span
              style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={e => composeNewSearch(e)}>
              Click here
            </span>{' '}
            to create new one{' '}
          </p>
        ) : (
          <div style={{ justifySelf: 'center' }}>
            <BeatLoader size={8} />
          </div>
        )}
      </List>
    </div>
  );
};

export default memo(TopicSearchHistory);
